import React, { useState, useEffect } from 'react';
import {
  ActivityStatusType,
  CitizenActivity,
  CitizenDto,
  CourseListDto,
  CreateCourseEnrollmentDto,
  CreateCourseEnrollmentsRequest,
  ReportType,
} from '../../../../api-service/clients';
import { BulkCourseEnrollmentComponent } from './bulk-course-enrollment-component';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { ISelectedEntity } from '../../../../components/unitoolbar/actions/interfaces';
import { useFeatureToggles } from '../../../../feature-toggles';

const attendanceClient = moduleClientFactory.createAttendanceClient();
const citizenClient = moduleClientFactory.createCitizenClient(true);
const offerAndActivityClient = moduleClientFactory.createOfferAndActivityClient(true);

export const bulkCourseEnrollmentCitizenIds = 'bulkCourseEnrollmentCitizenIds';
const missingCaseError = 'Aktiviteten er ikke tilknyttet en sag.';
const inactiveActivityError = 'Aktiviteten er ikke aktiv';
const inactiveActivityStatuses = [ActivityStatusType.Draft, ActivityStatusType.Cancelled, ActivityStatusType.Discontinued];
export type CreateCourseEnrollmentDtoErrors = { [P in keyof CreateCourseEnrollmentDto]?: string } & { courseId?: string };

interface CitizenListItem extends CitizenDto {
  activityName?: string;
  startDate?: Date | string | undefined;
  endDate?: Date | string | undefined;
  caseId?: string;
  error?: string;
}

const defaultEnrollmentRequest: CreateCourseEnrollmentDto = {
  createAdvisIfNotOnTime: false,
  createFollowUpAdvisOnMissingAttendance: false,
  createNegativeEventOnMissingAttendance: false,
  followDfdgActivityPeriod: true,
  hasDutyToWork: false,
  desiredReportType: ReportType.Weekly,
} as CreateCourseEnrollmentDto;
export function BulkCourseEnrollment() {
  const [courses, setCourses] = useState<CourseListDto[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<CourseListDto | undefined>();
  const [enrollmentRequest, setEnrollmentRequest] = useState<CreateCourseEnrollmentDto>(defaultEnrollmentRequest);
  const [citizenActivityIds, setCitizenActivityIds] = useState<CitizenActivity[]>([]);
  const [citizens, setCitizens] = useState<CitizenListItem[]>([]);
  const [isCitizenLoading, setIsCitizenLoading] = useState(true);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<CreateCourseEnrollmentDtoErrors>({} as CreateCourseEnrollmentDtoErrors);
  const [errorCitizens, setErrorCitizens] = useState<CitizenListItem[]>([]);
  const { isFeatureEnabled } = useFeatureToggles();
  usePostToTab<ISelectedEntity[]>(bulkCourseEnrollmentCitizenIds, onMessageReceived);

  useEffect(() => {
    loadCourses();
  }, []);

  async function onMessageReceived(ids: ISelectedEntity[]) {
    if (!ids) {
      return;
    }
    const mapped = ids.map(x => ({ activityId: x.entityId, citizenId: x.rootEntityId })).filter(x => x.citizenId !== undefined) as CitizenActivity[];
    setCitizenActivityIds(mapped);
    const citizenIds = mapped.map(x => x.citizenId);
    await loadCitizens(citizenIds);
    await updateCitizenListWithActivity(
      mapped.map(x => x.activityId).filter((x): x is string => !!x),
      citizenIds
    );
  }

  async function loadCitizens(citizenIds: string[]) {
    try {
      const result = await citizenClient.getByIds({ citizenIds });
      setCitizens(result?.citizens ?? []);
    } finally {
      setIsCitizenLoading(false);
    }
  }

  async function updateCitizenListWithActivity(activityIds: string[], citizenIds: string[]) {
    try {
      const result = await offerAndActivityClient.getActivitiesBasicInfo({ activityIds, citizenIds });
      result?.activities?.forEach(activity => {
        setCitizens(prev =>
          prev.map(c =>
            c.citizenId === activity.citizenId
              ? {
                  ...c,
                  activityName: activity.title,
                  startDate: activity.startDate,
                  endDate: activity.endDate,
                  caseId: activity.caseId,
                  error: inactiveActivityStatuses.includes(activity.status) ? inactiveActivityError : undefined,
                }
              : c
          )
        );
      });
    } finally {
      setIsCitizenLoading(false);
    }
  }

  async function loadCourses() {
    try {
      setIsLoadingCourses(true);
      const courseList = isFeatureEnabled('CourseEnrollmentsInFasitAndNotConnect') ? await loadFasitCourses() : await loadConnectCourses();
      setCourses(courseList);
    } finally {
      setIsLoadingCourses(false);
    }
  }

  async function loadFasitCourses() {
    const result = await attendanceClient.getActiveCourses({});
    return (result?.courses?.map(x => ({ courseId: x.courseId, name: x.name })) ?? []) as CourseListDto[];
  }

  async function loadConnectCourses() {
    const result = await attendanceClient.getCoursesFromConnect();
    return (result?.courses?.filter(x => x.active).map(x => ({ courseId: x.courseId, name: x.name })) ?? []) as CourseListDto[];
  }

  function handleonEnrollmentChange<K extends keyof CreateCourseEnrollmentDto>(name: K, value: CreateCourseEnrollmentDto[K]) {
    setEnrollmentRequest(prev => ({ ...prev, [name]: value }));
  }

  function handleValidate(): boolean {
    const newErrors: CreateCourseEnrollmentDtoErrors = {};
    if (!enrollmentRequest.followDfdgActivityPeriod && !enrollmentRequest.beginAt) {
      newErrors.beginAt = 'Startdato er påkrævet';
    }
    if (!enrollmentRequest.followDfdgActivityPeriod && !enrollmentRequest.endAt) {
      newErrors.endAt = 'Slutdato er påkrævet';
    }
    if (!enrollmentRequest.followDfdgActivityPeriod && enrollmentRequest.beginAt && enrollmentRequest.endAt && enrollmentRequest.endAt < enrollmentRequest.beginAt) {
      newErrors.endAt = 'Slutdato skal være efter startdato';
    }
    if (!selectedCourse?.courseId) {
      newErrors.courseId = 'Hold er påkrævet';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !!selectedCourse?.courseId;
  }

  async function handleCreate() {
    try {
      setIsSubmitting(true);
      if (selectedCourse) {
        const courseEnrollments: CreateCourseEnrollmentDto[] = [];
        const skippedCitizens: CitizenListItem[] = [];
        const requiresCase = isFeatureEnabled('CourseEnrollmentsInFasitAndNotConnect');

        citizenActivityIds.forEach(ca => {
          const citizen = citizens.find(c => c.citizenId === ca.citizenId);
          if (!citizen) {
            return;
          }
          if (citizen.error) {
            skippedCitizens.push(citizen);
            return;
          }
          if (requiresCase && !citizen.caseId) {
            skippedCitizens.push({ ...citizen, error: missingCaseError });
            return;
          }
          courseEnrollments.push({
            citizenId: ca.citizenId!,
            caseId: citizen.caseId!,
            activityId: ca.activityId,
            courseId: selectedCourse.courseId,
            beginAt: enrollmentRequest.followDfdgActivityPeriod ? (citizen.startDate as Date) : enrollmentRequest.beginAt,
            endAt: enrollmentRequest.followDfdgActivityPeriod ? (citizen.endDate as Date) : enrollmentRequest.endAt,
            followDfdgActivityPeriod: enrollmentRequest.followDfdgActivityPeriod,
            createFollowUpAdvisOnMissingAttendance: enrollmentRequest.createFollowUpAdvisOnMissingAttendance,
            createNegativeEventOnMissingAttendance: enrollmentRequest.createNegativeEventOnMissingAttendance,
            createAdvisIfNotOnTime: enrollmentRequest.createAdvisIfNotOnTime,
            hasDutyToWork: enrollmentRequest.hasDutyToWork,
            desiredReportType: enrollmentRequest.desiredReportType,
            isDeleted: false,
            isActive: true,
          } as CreateCourseEnrollmentDto);
        });

        if (courseEnrollments.length === 0) {
          setErrorCitizens(skippedCitizens);
          return;
        }

        const request: CreateCourseEnrollmentsRequest = { courseEnrollments };
        const response = await attendanceClient.createCourseEnrollments(request);

        const { citizenActivityIdsErrors } = response;
        const errorCitizenIds = new Set(citizenActivityIdsErrors?.map(x => x.citizenId) ?? []);
        const failed = citizens
          .filter(c => errorCitizenIds.has(c.citizenId))
          .map(c => ({ ...c, error: citizenActivityIdsErrors?.find(e => e.citizenId === c.citizenId)?.errorText }));

        if (skippedCitizens.length > 0 || failed.length > 0) {
          setErrorCitizens([...skippedCitizens, ...failed]);
        } else {
          window.close();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return isFeatureEnabled('EnableAttendanceMassActionInFrontend') ? (
    <BulkCourseEnrollmentComponent
      courses={courses}
      selectedCourse={selectedCourse}
      onCourseChange={setSelectedCourse}
      enrollmentRequest={enrollmentRequest}
      onEnrollmentChange={handleonEnrollmentChange}
      onValidate={handleValidate}
      onOk={handleCreate}
      isSubmitting={isSubmitting}
      errors={errors}
      isLoadingCourses={isLoadingCourses}
      isCitizenLoading={isCitizenLoading}
      citizens={citizens}
      errorCitizens={errorCitizens}
      onErrorDialogClose={() => window.close()}
    />
  ) : (
    <div />
  );
}
export default BulkCourseEnrollment;
